/**
 * Delayed commit with an undo window (PRD §4.8, B.23). Pure and framework-free,
 * so it is unit-tested without a DOM; `hooks/useUndoableMutation` wraps it.
 *
 * `schedule(vars)` does NOT call the server. It waits `windowMs` (6 s by
 * default) and only then runs `commit(vars)`. `undo()` inside the window
 * cancels it: nothing was sent, so nothing has to be reversed (closing a ticket
 * never needs a reopen). `flush()` commits at once (e.g. the page is being left).
 */
export const UNDO_WINDOW_MS = 6000;

export type PendingState = 'pending' | 'undone' | 'committing' | 'committed' | 'failed';

export interface PendingCommit {
  readonly id: number;
  state(): PendingState;
  /** Cancel inside the window. Returns false when it is too late (already sent). */
  undo(): boolean;
  /** Send now instead of waiting for the window to end. */
  flush(): Promise<void>;
}

export interface Timers {
  setTimeout: (fn: () => void, ms: number) => unknown;
  clearTimeout: (handle: unknown) => void;
}

export interface UndoableCommitOptions<V, R> {
  commit: (vars: V) => Promise<R>;
  windowMs?: number;
  onCommitted?: (result: R, vars: V, id: number) => void;
  onError?: (error: unknown, vars: V, id: number) => void;
  onUndone?: (vars: V, id: number) => void;
  /** Injected in tests; the global timers otherwise. */
  timers?: Timers;
}

export interface UndoableCommitter<V> {
  schedule(vars: V): PendingCommit;
  /** Commit every pending item now (unmount, pagehide). */
  flushAll(): Promise<void>;
  pendingCount(): number;
}

const defaultTimers: Timers = {
  setTimeout: (fn, ms) => setTimeout(fn, ms),
  clearTimeout: (h) => clearTimeout(h as ReturnType<typeof setTimeout>),
};

export function createUndoableCommit<V, R>(options: UndoableCommitOptions<V, R>): UndoableCommitter<V> {
  const windowMs = options.windowMs ?? UNDO_WINDOW_MS;
  const timers = options.timers ?? defaultTimers;
  const pending = new Map<number, () => Promise<void>>();
  let nextId = 0;

  const schedule = (vars: V): PendingCommit => {
    const id = ++nextId;
    let state: PendingState = 'pending';
    let running: Promise<void> | null = null;

    const run = (): Promise<void> => {
      if (running) return running;
      if (state !== 'pending') return Promise.resolve();
      timers.clearTimeout(handle);
      pending.delete(id);
      state = 'committing';
      running = Promise.resolve()
        .then(() => options.commit(vars))
        .then(
          (result) => { state = 'committed'; options.onCommitted?.(result, vars, id); },
          (error) => { state = 'failed'; options.onError?.(error, vars, id); },
        );
      return running;
    };

    const handle = timers.setTimeout(() => { void run(); }, windowMs);
    pending.set(id, run);

    return {
      id,
      state: () => state,
      undo: () => {
        if (state !== 'pending') return false;
        timers.clearTimeout(handle);
        pending.delete(id);
        state = 'undone';
        options.onUndone?.(vars, id);
        return true;
      },
      flush: run,
    };
  };

  return {
    schedule,
    flushAll: async () => { await Promise.all([...pending.values()].map((run) => run())); },
    pendingCount: () => pending.size,
  };
}
